import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Lock, Plus, Pencil, Trash2, Loader2, Eye, EyeOff, ArrowLeft, Sparkles, Calendar, Clock } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  'Costs & Pricing', 'Legal & Compliance', 'Speed & Process',
  'Workers & Industries', 'Volume & Flexibility', 'Payroll & HR',
  'Quality & Reliability', 'Comparing Options', 'Location & Regional',
  'Misconceptions & Concerns', 'Industry News', 'Career Tips',
];

const AdminBlogPage = () => {
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [formData, setFormData] = useState({
    title: '', excerpt: '', content: '', category: 'Construction',
    author: 'WorkNation Labour Ltd', published: true,
    meta_description: '', tags: '',
  });

  const headers = { 'x-admin-password': password };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/blog`, { headers });
      setPosts(res.data.posts);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to fetch posts', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      await axios.get(`${API}/admin/blog`, { headers: { 'x-admin-password': password } });
      setAuthenticated(true);
    } catch {
      toast({ title: 'Access Denied', description: 'Invalid admin password', variant: 'destructive' });
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => { if (authenticated) { fetchPosts(); fetchSchedule(); } }, [authenticated]);

  const fetchSchedule = async () => {
    try {
      const res = await axios.get(`${API}/admin/blog/schedule`, { headers });
      setSchedule(res.data);
    } catch {}
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/blog/generate`, {}, { headers });
      toast({ title: 'Post Generated!', description: `Published: "${res.data.post.title}"` });
      fetchPosts();
      fetchSchedule();
    } catch (err) {
      toast({ title: 'Generation Failed', description: err.response?.data?.detail || 'Check logs', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', excerpt: '', content: '', category: 'Construction',
      author: 'WorkNation Labour Ltd', published: true,
      meta_description: '', tags: '',
    });
    setEditing(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      tags: formData.tags.split(',').map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await axios.put(`${API}/blog/${editing}`, payload, { headers });
        toast({ title: 'Updated', description: 'Blog post updated successfully' });
      } else {
        await axios.post(`${API}/blog`, payload, { headers });
        toast({ title: 'Created', description: 'Blog post created successfully' });
      }
      resetForm();
      fetchPosts();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to save post', variant: 'destructive' });
    }
  };

  const handleEdit = (post) => {
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      author: post.author,
      published: post.published,
      meta_description: post.meta_description || '',
      tags: (post.tags || []).join(', '),
    });
    setEditing(post.slug);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${API}/blog/${slug}`, { headers });
      toast({ title: 'Deleted', description: 'Blog post deleted' });
      fetchPosts();
    } catch {
      toast({ title: 'Error', description: 'Failed to delete post', variant: 'destructive' });
    }
  };

  // Auth screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-40 pb-20 px-6 flex justify-center">
          <div className="w-full max-w-sm">
            <div className="bg-white rounded-2xl shadow-xl p-8 border-t-4 border-[#003366]">
              <div className="flex items-center justify-center mb-6">
                <Lock className="text-[#003366]" size={32} />
              </div>
              <h1 className="text-2xl font-bold text-[#003366] text-center mb-6">Blog Admin</h1>
              <form onSubmit={handleAuth} className="space-y-4">
                <div>
                  <Label htmlFor="admin-password" className="text-gray-700 font-semibold">Admin Password</Label>
                  <Input
                    id="admin-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Enter admin password"
                    className="mt-1"
                    data-testid="admin-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={authLoading}
                  className="w-full bg-[#003366] hover:bg-[#004488] text-white"
                  data-testid="admin-login-btn"
                >
                  {authLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Lock className="mr-2" size={18} />}
                  Access Admin Panel
                </Button>
              </form>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Blog Admin - WorkNation Labour Ltd</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Header />
      <main className="pt-28 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold text-[#003366]">Blog Admin</h1>
            <div className="flex gap-2">
              <a href="/blog" className="flex items-center text-sm text-gray-500 hover:text-[#003366]">
                <ArrowLeft size={16} className="mr-1" /> View Blog
              </a>
              {!showForm && (
                <Button onClick={() => setShowForm(true)} className="bg-[#003366] hover:bg-[#004488] text-white" data-testid="new-post-btn">
                  <Plus size={18} className="mr-1" /> New Post
                </Button>
              )}
            </div>
          </div>

          {/* Auto Blog Status */}
          <div className="bg-white rounded-xl shadow-md p-5 mb-8 border border-gray-100" data-testid="auto-blog-status">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Sparkles className="text-green-600" size={20} />
                </div>
                <div>
                  <h2 className="font-bold text-[#003366] text-lg">Auto Blog Generator</h2>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                    <span className="flex items-center">
                      <span className={`w-2 h-2 rounded-full mr-1.5 ${schedule?.scheduler_running ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      {schedule?.scheduler_running ? 'Active' : 'Inactive'}
                    </span>
                    <span className="flex items-center">
                      <Calendar size={13} className="mr-1" />
                      {schedule?.schedule || 'Every Monday 9 AM UTC'}
                    </span>
                    {schedule?.next_scheduled_run && (
                      <span className="flex items-center">
                        <Clock size={13} className="mr-1" />
                        Next: {new Date(schedule.next_scheduled_run).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                    )}
                    <span>{schedule?.total_posts || 0} posts total</span>
                  </div>
                </div>
              </div>
              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="generate-now-btn"
              >
                {generating ? (
                  <><Loader2 className="animate-spin mr-2" size={16} /> Generating...</>
                ) : (
                  <><Sparkles size={16} className="mr-2" /> Generate Now</>
                )}
              </Button>
            </div>
          </div>

          {/* Post form */}
          {showForm && (
            <div className="bg-white rounded-xl shadow-md p-6 mb-8 border-l-4 border-[#003366]" data-testid="blog-post-form">
              <h2 className="text-xl font-bold text-[#003366] mb-4">{editing ? 'Edit Post' : 'Create New Post'}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="font-semibold">Title *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required data-testid="post-title-input" />
                </div>
                <div>
                  <Label className="font-semibold">Category *</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 mt-1"
                    data-testid="post-category-select"
                  >
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="font-semibold">Excerpt *</Label>
                  <Textarea value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} required rows={3} data-testid="post-excerpt-input" />
                </div>
                <div>
                  <Label className="font-semibold">Content (HTML) *</Label>
                  <Textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} required rows={12} className="font-mono text-sm" data-testid="post-content-input" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-semibold">Meta Description</Label>
                    <Input value={formData.meta_description} onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })} placeholder="SEO description (max 160 chars)" />
                  </div>
                  <div>
                    <Label className="font-semibold">Tags (comma-separated)</Label>
                    <Input value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })} placeholder="construction, Vancouver, jobs" data-testid="post-tags-input" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="published" checked={formData.published} onChange={(e) => setFormData({ ...formData, published: e.target.checked })} />
                  <Label htmlFor="published" className="font-semibold cursor-pointer">Published</Label>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="submit" className="bg-[#003366] hover:bg-[#004488] text-white" data-testid="save-post-btn">
                    {editing ? 'Update Post' : 'Create Post'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                </div>
              </form>
            </div>
          )}

          {/* Posts list */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin text-[#003366]" size={36} />
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-2">{posts.length} post{posts.length !== 1 ? 's' : ''} total</p>
              {posts.map((post) => (
                <div key={post.id} className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 flex items-center justify-between" data-testid={`admin-post-${post.slug}`}>
                  <div className="flex-1 min-w-0 mr-4">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-[#003366] truncate">{post.title}</h3>
                      {!post.published && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700">
                          <EyeOff size={10} className="mr-1" /> Draft
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span>{post.category}</span>
                      <span>{new Date(post.published_at).toLocaleDateString('en-CA')}</span>
                      <span>{post.reading_time} min read</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer" className="p-2 text-gray-400 hover:text-[#003366]" title="View">
                      <Eye size={18} />
                    </a>
                    <button onClick={() => handleEdit(post)} className="p-2 text-gray-400 hover:text-blue-600" title="Edit" data-testid={`edit-${post.slug}`}>
                      <Pencil size={18} />
                    </button>
                    <button onClick={() => handleDelete(post.slug)} className="p-2 text-gray-400 hover:text-red-600" title="Delete" data-testid={`delete-${post.slug}`}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminBlogPage;
